import { getFeatureAnalysis } from "./solarAnalysisUtils"

type HeatLossCalculation = any
type EnergyCalculation = any
type SolarAnalysis = any
type EnergyEstimate = any

export const getAvailableScenarios = (
  heatLossCalculations: HeatLossCalculation[]
) =>
  Array.from(
    new Set(
      heatLossCalculations.map((calc) => calc.calculationScenario).filter(Boolean)
    )
  )

export const getLatestData = (
  heatLossCalculations: HeatLossCalculation[],
  energyCalculations: EnergyCalculation[],
  solarAnalyses: SolarAnalysis[],
  selectedScenario: string
) => {
  const latestHeatLoss =
    heatLossCalculations.find(
      (calc) => calc.calculationScenario === selectedScenario
    ) || null
  const latestEnergy =
    energyCalculations.find((calc) => calc.numOccupiers === 2) ||
    energyCalculations[0] ||
    null
  const latestSolar = getFeatureAnalysis(solarAnalyses)
  return { latestHeatLoss, latestEnergy, latestSolar }
}

export const getSelectedEnergyEstimate = (
  energyEstimates: EnergyEstimate[],
  shouldUseEnergyEstimates: boolean
) => {
  if (!shouldUseEnergyEstimates) return null
  return (
    energyEstimates.find((estimate) => estimate.scenario === "default") ||
    energyEstimates[0] ||
    null
  )
}

const parseNumber = (value?: string | number | null) =>
  value !== undefined && value !== null ? parseFloat(value as string) : 0

export const calculateTotalElectricityDemand = ({
  latestHeatLoss,
  latestEnergy,
  shouldUseEnergyEstimates,
  selectedEnergyEstimate
}: {
  latestHeatLoss: HeatLossCalculation | null
  latestEnergy: EnergyCalculation | null
  shouldUseEnergyEstimates: boolean
  selectedEnergyEstimate: EnergyEstimate | null
}) => {
  if (!latestHeatLoss || !latestEnergy) return 0

  const totalElectrifiedHeatDemand =
    (latestHeatLoss as any)?.totalElectrifiedHeatDemandKwh ??
    latestHeatLoss?.totalAnnualHeatDemandKwh

  const heatLossElectrified = parseNumber(totalElectrifiedHeatDemand)

  if (shouldUseEnergyEstimates) {
    if (!selectedEnergyEstimate) return Math.round(heatLossElectrified)
    const estimateElectricity = parseNumber(
      selectedEnergyEstimate.totalElectricityKwh
    )

    const estimateGas = parseNumber(
      selectedEnergyEstimate.totalHouseholdGasKwh
    )

    const heatLossCop = parseNumber((latestHeatLoss as any)?.copUsed)
    const gasElectrified = heatLossCop > 0 ? estimateGas / heatLossCop : 0

    return Math.round(heatLossElectrified + estimateElectricity + gasElectrified)
  }

  const totalHouseholdElectricity =
    parseNumber(latestEnergy.totalElectricityKwh) +
    parseNumber(latestEnergy.totalGasElectrified)

  return Math.round(heatLossElectrified + totalHouseholdElectricity)
}

export const calculateSolarGeneration = (latestSolar: SolarAnalysis | null) =>
  latestSolar ? Math.round(parseNumber(latestSolar.recommendedPanelsOutputKwh)) : 0

export const calculateNetGridImport = (
  totalElectricityDemand: number,
  solarGeneration: number
) => Math.round(totalElectricityDemand - solarGeneration)

export const getZeroBillPotential = (netImport: number) => {
  if (netImport < 0) return { label: "High", color: "green" }
  if (netImport <= 1000) return { label: "Medium", color: "orange" }
  return { label: "Low", color: "red" }
}

